import { z } from 'zod';

/**
 * The event log. Everything the field app produces is an immutable, append-only fact.
 *
 * This is the decision the whole offline design rests on. Storing mutable state
 * ("cart X is at site Y since Z") makes two offline devices irreconcilable: on sync you
 * have two truths and must discard one. Storing events means two devices produce two rows,
 * both arrive, nothing is overwritten, and current position is *derived* (see projection.ts).
 *
 * A round is modelled the same way — as an opened event, member scans, and a closing event —
 * rather than as a long-lived transaction held open across hours and app restarts. Nothing
 * is kept open, so nothing has to survive anything.
 */

const base = {
  /** Generated on the client. This is the idempotency key: replaying a send is free. */
  id: z.string().uuid(),
  /** Device clock at the moment of the action. See projection.ts on ordering and skew. */
  at: z.string().datetime(),
  device_id: z.string().uuid(),
  /** Identity cached at login, so attribution survives being offline for days. */
  user_id: z.string().min(1).max(200),
};

export const RoundOpened = z.object({
  ...base,
  type: z.literal('round_opened'),
  round_id: z.string().uuid(),
  site_code: z.string().regex(/^SITE-\d{6}$/),
  /**
   * What this opening is for.
   *
   * `sweep` is the full tour of a site, the one that concludes on absences. `correction` is
   * the cart found in a corner after the sweep was closed: a scan still has to belong to a
   * round — that is how the log is shaped — but it must not look like a sweep to anybody.
   * It never becomes the device's round in progress, never warns another operator that
   * someone is touring here, and concludes nothing when it ends.
   *
   * Optional, and absent means `sweep`: every event written before this existed stays valid.
   */
  mode: z.enum(['sweep', 'correction']).optional(),
});

export const CartScanned = z.object({
  ...base,
  type: z.literal('cart_scanned'),
  round_id: z.string().uuid(),
  cart_code: z.string().regex(/^CHT-\d{6}$/),
  /** Denormalised so a scan is interpretable without resolving its round. */
  site_code: z.string().regex(/^SITE-\d{6}$/),
});

export const RoundClosed = z.object({
  ...base,
  type: z.literal('round_closed'),
  round_id: z.string().uuid(),
  site_code: z.string().regex(/^SITE-\d{6}$/),
  /**
   * Carts the operator expected at this site but did not find, computed ON THE DEVICE at
   * closure time. This is deliberate: the operator closes the round standing in the
   * warehouse with no signal and must see the answer immediately. Were the server to
   * compute it, a round closed Tuesday and synced Friday would be diffed against a
   * catalogue that had already moved on.
   */
  missing: z.array(z.string().regex(/^CHT-\d{6}$/)).max(1000),
  expected_count: z.number().int().min(0),
  scanned_count: z.number().int().min(0),
});

export const RoundAbandoned = z.object({
  ...base,
  type: z.literal('round_abandoned'),
  round_id: z.string().uuid(),
  site_code: z.string().regex(/^SITE-\d{6}$/),
  /**
   * Abandoning keeps the scans and discards only the conclusion: every cart_scanned in an
   * abandoned round still counts as a confirmation. Absence is simply not inferred from an
   * incomplete sweep.
   */
  reason: z.enum(['interrupted', 'wrong_site', 'other', 'correction']).optional(),
});

/**
 * A closed round taken up again.
 *
 * A finished round is not edited in place — nothing in this log is. Reopening *withdraws
 * its conclusion*: the round accepts scans again and the next `round_closed` restates what
 * was found. Two devices that both reopen the same round still produce two facts that
 * merge, which is the whole reason the log is append-only.
 *
 * Deliberately time-limited (see REOPEN_WINDOW_HOURS): reopening a sweep from last month
 * and re-closing it would compare it against a catalogue that has moved on, and declare
 * carts missing that had simply been moved since.
 */
export const RoundReopened = z.object({
  ...base,
  type: z.literal('round_reopened'),
  round_id: z.string().uuid(),
  site_code: z.string().regex(/^SITE-\d{6}$/),
});

/**
 * A scan withdrawn: the wrong cart was read, or one was counted twice.
 *
 * Keyed by the *scan's* id rather than by cart code, so scanning the same cart again
 * afterwards is an ordinary new fact rather than an ambiguity. The scan itself stays in the
 * log — this says it should not be counted, not that it never happened.
 */
export const ScanRetracted = z.object({
  ...base,
  type: z.literal('scan_retracted'),
  round_id: z.string().uuid(),
  site_code: z.string().regex(/^SITE-\d{6}$/),
  /** The `cart_scanned` event being withdrawn. */
  scan_id: z.string().uuid(),
  /** Denormalised, so a retraction is interpretable without resolving the scan. */
  cart_code: z.string().regex(/^CHT-\d{6}$/),
  reason: z.enum(['mis_scan', 'other']).optional(),
});

export const ClientEvent = z.discriminatedUnion('type', [
  RoundOpened,
  CartScanned,
  RoundClosed,
  RoundAbandoned,
  RoundReopened,
  ScanRetracted,
]);
export type ClientEvent = z.infer<typeof ClientEvent>;
export type EventType = ClientEvent['type'];

/** An event as the server hands it back: same facts plus arrival metadata. */
export const StoredEvent = z.intersection(
  ClientEvent,
  z.object({
    /** Monotonic server sequence — the sync cursor. */
    seq: z.number().int().positive(),
    /** Server clock on arrival. Used to detect a wrong device clock, never to reorder. */
    received_at: z.string().datetime(),
    /** Signed difference between device and server clock, in minutes. */
    clock_skew_minutes: z.number().int(),
  }),
);
export type StoredEvent = z.infer<typeof StoredEvent>;

export function isCartScanned(e: ClientEvent): e is z.infer<typeof CartScanned> {
  return e.type === 'cart_scanned';
}
export function isRoundClosed(e: ClientEvent): e is z.infer<typeof RoundClosed> {
  return e.type === 'round_closed';
}
export function isScanRetracted(e: ClientEvent): e is z.infer<typeof ScanRetracted> {
  return e.type === 'scan_retracted';
}
